import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { toast } from 'react-toastify';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { MOCK_PRODUCTS, generateForecast } from '../mock/mockData';
import CPMockBadge from './CPMockBadge';

// ---- Colour constants (matching BulkEditUpdated palette) ----
const C = {
  primary: '#522d6e',
  primaryLight: '#7c5aa4',
  white: '#ffffff',
  grey: '#e5e7eb',
  greyLight: '#f3f4f6',
  greyDark: '#6b7280',
  green: '#059669',
  greenBg: '#d1fae5',
  red: '#dc2626',
  redBg: '#fee2e2',
  rowAlt: '#F7F3FE',
  teal: '#028383',
};

// ---- Status badge styles ----
function statusBadgeStyle(isPreProvisional: boolean): React.CSSProperties {
  return isPreProvisional
    ? { fontSize: '10px', fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' as const }
    : { fontSize: '10px', fontWeight: 700, color: '#059669', background: '#d1fae5', border: '1px solid #6ee7b7', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' as const };
}

// ---- Inline styles ----
const s: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    padding: '0.75rem 1rem',
    background: C.white,
    borderRadius: '8px',
    border: `1px solid ${C.grey}`,
    gap: '1rem',
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  toolbarLabel: {
    fontWeight: 600,
    color: C.primary,
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  gridWrapper: {
    border: `1px solid ${C.grey}`,
    borderRadius: '8px',
    background: C.white,
    height: '60vh',
    overflowY: 'auto',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    background: C.primaryLight,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  th: {
    color: C.white,
    fontWeight: 500,
    fontSize: '12px',
    padding: '0.5rem 0.4rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  thLeft: {
    color: C.white,
    fontWeight: 500,
    fontSize: '12px',
    padding: '0.5rem 0.6rem',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  td: {
    textAlign: 'center',
    padding: '0.3rem 0.4rem',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.greyLight}`,
  },
  tdLeft: {
    textAlign: 'left',
    padding: '0.3rem 0.6rem',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.greyLight}`,
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  smallBtn: {
    height: '1.6rem',
    padding: '0 0.6rem',
    fontSize: '11px',
    borderRadius: '3px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  } as React.CSSProperties,
  btn: {
    height: '2rem',
    padding: '0 1rem',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
  } as React.CSSProperties,
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  // Modal styles
  modalField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '1rem',
  } as React.CSSProperties,
  modalLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  modalMonthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
    marginTop: '0.25rem',
  } as React.CSSProperties,
  modalMonthCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    position: 'relative',
  } as React.CSSProperties,
  modalMonthLabel: {
    fontSize: '11px',
    color: '#6b7280',
    fontWeight: 500,
  } as React.CSSProperties,
  cellInput: {
    width: '5rem',
    fontSize: '12px',
    height: '1.5rem',
    border: `1px solid ${C.grey}`,
    borderRadius: '3px',
    textAlign: 'right',
    padding: '0 0.3rem',
    outline: 'none',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  copyDot: {
    position: 'absolute',
    bottom: '3px',
    right: '5px',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: '10px',
    color: C.primaryLight,
    fontWeight: 'bold',
    userSelect: 'none',
  } as React.CSSProperties,
};

// ---- Types ----
interface ForecastRow {
  id: string;
  concept: string;
  ndc11: string;
  isPreProvisional: boolean;  // true = user-added pre-provisional, false = Active (PLM)
  values: Record<string, string>;
}

// ---- NDC11 formatter (XXXXX-XXXX-XX) ----
function formatNdc11(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 5) return digits;
  if (digits.length <= 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}-${digits.slice(9)}`;
}

// ---- Helpers ----
function fmtM(iso: string): string {
  const [y, m] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
}

function buildInitialRows(months: string[]): ForecastRow[] {
  const calendarForPeriod = months.map(m => ({ month: m, work_days: 22 }));
  const forecast = generateForecast(calendarForPeriod);
  const forecastMap: Record<string, Record<string, string>> = {};
  forecast.forEach(f => {
    if (!forecastMap[f.NDC11]) forecastMap[f.NDC11] = {};
    forecastMap[f.NDC11][f.ForecastPeriod] = String(f.ForecastBottles);
  });
  return MOCK_PRODUCTS.map((p, idx) => ({
    id: `${p.NDC11}-${idx}`,
    concept: p.Description,
    ndc11: p.NDC11,
    isPreProvisional: false,
    values: Object.fromEntries(months.map(m => [m, forecastMap[p.NDC11]?.[m] ?? ''])),
  }));
}

// ---- Component ----
const CPForecastTab = observer(() => {
  const store = useCapacityStore();

  // Planning period drives the 12-month columns — must be declared before months
  const [planningPeriod, setPlanningPeriod] = useState<Date>(() => {
    const ym = store.startYearMonth || '2026-04';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1);
  });

  // Months derived from the date picker, not from the store calendar
  const months = useMemo<string[]>(() => {
    const result: string[] = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(planningPeriod.getFullYear(), planningPeriod.getMonth() + i, 1);
      result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return result;
  }, [planningPeriod]);

  const [rows, setRows] = useState<ForecastRow[]>(() => buildInitialRows(months));

  // Re-build rows whenever the planning period changes (fills in values for new months)
  useEffect(() => {
    setRows(buildInitialRows(months));
  }, [months]);

  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [newConcept, setNewConcept] = useState('');
  const [newNdc, setNewNdc] = useState('');
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [newFocused, setNewFocused] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Handlers: main table (pre-provisional rows only) ----
  const setCellValue = useCallback((rowId: string, month: string, val: string) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, values: { ...r.values, [month]: val } } : r));
  }, []);

  const copyForward = useCallback((rowId: string, fromMonth: string) => {
    const mIdx = months.indexOf(fromMonth);
    if (mIdx < 0) return;
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const val = r.values[fromMonth] ?? '';
      const newVals = { ...r.values };
      for (let i = mIdx + 1; i < months.length; i++) newVals[months[i]] = val;
      return { ...r, values: newVals };
    }));
    setFocusedCell(null);
  }, [months]);

  const deleteRow = useCallback((rowId: string) => {
    setRows(prev => prev.filter(r => r.id !== rowId));
  }, []);

  const handleSaveAll = useCallback(async () => {
    setIsSaving(true);
    try {
      await store.applyPlanningSettings(store.startYearMonth, store.planningParams);
      toast.success('Forecast saved and recalculation started.', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [store]);

  // ---- Handlers: Add Product modal ----
  const openModal = useCallback(() => {
    setNewConcept('');
    setNewNdc('');
    setNewValues({});
    setNewFocused(null);
    setModalVisible(true);
  }, []);

  const copyForwardModal = useCallback((fromMonth: string) => {
    const mIdx = months.indexOf(fromMonth);
    if (mIdx < 0) return;
    const val = newValues[fromMonth] ?? '';
    const next = { ...newValues };
    for (let i = mIdx + 1; i < months.length; i++) next[months[i]] = val;
    setNewValues(next);
    setNewFocused(null);
  }, [months, newValues]);

  const handleAddProduct = useCallback(() => {
    if (!newConcept.trim() && !newNdc.trim()) return;
    const newRow: ForecastRow = {
      id: `custom-${Date.now()}`,
      concept: newConcept.trim() || '(Unnamed)',
      ndc11: newNdc.trim() || '—',
      isPreProvisional: true,
      values: { ...newValues },
    };
    setRows(prev => [newRow, ...prev]);
    setModalVisible(false);
    toast.info('Product added. Remember to click Save All to save your changes.', {
      position: 'bottom-right', autoClose: 4000,
    });
  }, [newConcept, newNdc, newValues]);

  // ---- Modal footer ----
  const modalFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button label="Cancel" severity="secondary" text size="small" onClick={() => setModalVisible(false)} />
      <Button
        label="Add Product"
        icon="pi pi-plus"
        size="small"
        style={{ background: C.primary, borderColor: C.primary }}
        onClick={handleAddProduct}
        disabled={!newConcept.trim() && !newNdc.trim()}
      />
    </div>
  );

  return (
    <div style={s.container}>
      {/* ---- Toolbar ---- */}
      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <CPMockBadge />
          <span style={s.toolbarLabel}>Planning Period:</span>
          <Calendar
            value={planningPeriod}
            onChange={e => { if (e.value instanceof Date) setPlanningPeriod(e.value); }}
            view="month"
            dateFormat="MM yy"
            showIcon
            readOnlyInput
            style={{ width: '185px' }}
            panelStyle={{ minWidth: '280px' }}
          />
        </div>
        <button
          style={{ ...s.btn, background: C.primary, color: C.white }}
          onClick={openModal}
        >
          <i className="pi pi-plus" /> Add Product
        </button>
      </div>

      {/* ---- Grid ---- */}
      <div style={s.gridWrapper}>
        <table style={s.table}>
          <thead style={s.thead}>
            <tr>
              <th style={{ ...s.thLeft, width: '160px', minWidth: '160px' }}>Concept</th>
              <th style={{ ...s.th, width: '110px' }}>NDC11</th>
              <th style={{ ...s.th, width: '130px' }}>Status</th>
              {months.map(m => (
                <th key={m} style={{ ...s.th, minWidth: '5.5rem' }}>{fmtM(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3 + months.length} style={{ padding: '3rem', textAlign: 'center', color: C.greyDark }}>
                  No products. Click "Add Product" to add one.
                </td>
              </tr>
            )}
            {rows.map((row, rIdx) => {
              const bg = rIdx % 2 ? C.rowAlt : C.white;
              return (
                <tr key={row.id} style={{ background: bg }}>
                  <td style={{ ...s.tdLeft, maxWidth: '160px' }} title={row.concept}>{row.concept}</td>
                  <td style={{ ...s.td, fontSize: '11px' }}>{row.ndc11}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
                      <span style={statusBadgeStyle(row.isPreProvisional)}>
                        {row.isPreProvisional ? 'Pre-provisional' : 'Active'}
                      </span>
                      {row.isPreProvisional && (
                        <button
                          onClick={() => deleteRow(row.id)}
                          title="Remove pre-provisional product"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#dc2626', padding: '2px 3px', borderRadius: 3,
                            display: 'inline-flex', alignItems: 'center',
                          }}
                        >
                          <i className="pi pi-trash" style={{ fontSize: 11 }} />
                        </button>
                      )}
                    </div>
                  </td>
                  {months.map((m, mIdx) => {
                    const cellKey = `${row.id}-${m}`;
                    const isFocused = focusedCell === cellKey;
                    return (
                      <td key={m} style={{ ...s.td, position: 'relative' }}>
                        {row.isPreProvisional ? (
                          <>
                            <input
                              type="text"
                              style={{ ...s.cellInput, borderColor: isFocused ? C.primaryLight : C.grey }}
                              value={row.values[m] ?? ''}
                              onFocus={() => setFocusedCell(cellKey)}
                              onBlur={() => {
                                if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                                blurTimerRef.current = setTimeout(() => {
                                  setFocusedCell(prev => prev === cellKey ? null : prev);
                                }, 150);
                              }}
                              onChange={e => setCellValue(row.id, m, e.target.value)}
                            />
                            {isFocused && mIdx < months.length - 1 && (
                              <span
                                title="Copy value to all months to the right"
                                style={s.copyDot}
                                onMouseDown={e => { e.preventDefault(); copyForward(row.id, m); }}
                              >
                                &#9679;
                              </span>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#374151' }}>
                            {row.values[m] ? Number(row.values[m]).toLocaleString() : '—'}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Bottom Save bar ---- */}
      <div style={s.bottomBar}>
        <button
          style={{ ...s.btn, background: C.primary, color: C.white, opacity: isSaving ? 0.7 : 1 }}
          onClick={handleSaveAll}
          disabled={isSaving}
        >
          {isSaving
            ? <><i className="pi pi-spin pi-spinner" /> Updating Dashboard...</>
            : <><i className="pi pi-save" /> Save & Update Dashboard</>
          }
        </button>
      </div>

      {/* ---- Add Product Modal ---- */}
      <Dialog
        header="Add New Product"
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '560px' }}
        footer={modalFooter}
        modal
      >
        <div style={{ padding: '0.25rem 0' }}>
          <div style={s.modalField}>
            <label style={s.modalLabel}>Concept *</label>
            <InputText
              value={newConcept}
              onChange={e => setNewConcept(e.target.value)}
              placeholder="e.g. Tramadol HCl 50mg Tablets"
              style={{ fontSize: '13px', width: '100%' }}
            />
          </div>
          <div style={s.modalField}>
            <label style={s.modalLabel}>NDC11 *</label>
            <InputText
              value={newNdc}
              onChange={e => setNewNdc(formatNdc11(e.target.value))}
              placeholder="e.g. 51407-0101-01"
              maxLength={13}
              style={{ fontSize: '13px', width: '100%', letterSpacing: '0.04em' }}
            />
          </div>
          <div style={s.modalField}>
            <label style={s.modalLabel}>Monthly Forecast Quantities</label>
            <p style={{ fontSize: '11px', color: C.greyDark, margin: '0 0 0.5rem' }}>
              Enter quantities for each month. Focus a cell then click ● to copy forward.
            </p>
            <div style={s.modalMonthGrid}>
              {months.map((m, mIdx) => {
                const isNewFocused = newFocused === m;
                return (
                  <div key={m} style={s.modalMonthCell}>
                    <span style={s.modalMonthLabel}>{fmtM(m)}</span>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        style={{
                          ...s.cellInput,
                          width: '100%',
                          borderColor: isNewFocused ? C.primaryLight : C.grey,
                        }}
                        value={newValues[m] ?? ''}
                        onFocus={() => setNewFocused(m)}
                        onBlur={() => {
                          if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                          blurTimerRef.current = setTimeout(() => {
                            setNewFocused(prev => prev === m ? null : prev);
                          }, 150);
                        }}
                        onChange={e => setNewValues(prev => ({ ...prev, [m]: e.target.value }))}
                      />
                      {isNewFocused && mIdx < months.length - 1 && (
                        <span
                          title="Copy to all subsequent months"
                          style={{ ...s.copyDot, fontSize: '16px' }}
                          onMouseDown={ev => {
                            ev.preventDefault();
                            copyForwardModal(m);
                          }}
                        >
                          &#9679;
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default CPForecastTab;
